/**
 * HttpService - Centralized HTTP client with authentication and retries
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import env from '../../config/env';
import LocalStorageService from './LocalStorageService';

// Custom HttpError class to provide more context
export class HttpError extends Error {
  status?: number;
  data?: any;
  originalError: AxiosError;

  constructor(axiosError: AxiosError) {
    super(axiosError.message);
    this.name = 'HttpError';
    this.status = axiosError.response?.status;
    this.data = axiosError.response?.data;
    this.originalError = axiosError;
  }

  isNetworkError(): boolean {
    return this.originalError.message === 'Network Error';
  }

  isTimeoutError(): boolean {
    return this.originalError.code === 'ECONNABORTED';
  }
}

// Request options with error handlers
export interface HttpRequestOptions extends AxiosRequestConfig {
  retries?: number;
  retryDelay?: number;
}

// Additional response metadata
export interface HttpResponse<T = any> {
  data: T;
  status: number;
  headers: Record<string, string>;
}

// Centralized HttpService
class HttpService {
  private axiosInstance: AxiosInstance;
  private maxRetries: number = 2;
  private retryDelay: number = 1000;

  private API_URL: string;
  
  constructor() {
    // Create a custom axios instance
    let base = (env.API_URL || '').replace(/\/+$/, '');
    if (base.endsWith('/api')) {
      console.warn('[HttpService] API_URL ends with /api – please set host without /api; trimming for now');
      base = base.replace(/\/api$/, '');
    }
    this.API_URL = base;
    this.axiosInstance = axios.create({
      baseURL: this.API_URL,
      withCredentials: false,
      paramsSerializer: {
        serialize(params: Record<string, unknown>) {
          const usp = new URLSearchParams();
          Object.entries(params || {}).forEach(([key, value]) => {
            if (value == null || value === '') return;
            if (Array.isArray(value)) usp.set(key, (value as any[]).join(','));
            else usp.set(key, String(value));
          });
          return usp.toString();
        }
      }
    });

    this.setupInterceptors();
  }

  // Generic request method
  async request<T = any>(config: HttpRequestOptions): Promise<HttpResponse<T>> {
    const { retries = this.maxRetries, retryDelay = this.retryDelay, ...axiosConfig } = config;
    let retryCount = 0;
    
    const attemptRequest = async (): Promise<HttpResponse<T>> => {
      try {
        if (process.env.NODE_ENV !== 'production') {
          try {
            const finalUrl = (this.axiosInstance as any).getUri(axiosConfig);
            console.debug('[HttpService] →', (axiosConfig.method || 'GET').toString().toUpperCase(), finalUrl);
          } catch {}
        }
        // Guards: nur /api/ Pfade zulassen (Dev-Only Warnung)
        if (typeof axiosConfig.url === 'string') {
          const u = axiosConfig.url;
          // nur debug logs, keine Warnungen (zu laut)
          // trailing slash wird pro Service korrekt gesetzt
        }

        const response = await this.axiosInstance.request<T>(axiosConfig);
        return {
          data: response.data,
          status: response.status,
          headers: response.headers as Record<string, string>,
        };
      } catch (error) {
        // Cast to AxiosError
        const axiosError = error as AxiosError;
        const httpError = new HttpError(axiosError);
        
        // Check if we should retry (for network errors or 5xx responses)
        const shouldRetry = 
          retryCount < retries && 
          (httpError.isNetworkError() || 
          (httpError.status && httpError.status >= 500));

        if (shouldRetry) {
          retryCount++;
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, retryCount - 1)));
          return attemptRequest();
        }

        // Re-throw the error if not swallowed
        throw httpError;
      }
    };

    return attemptRequest();
  }

  // Convenience methods
  async get<T = any>(url: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>({ ...options, method: 'GET', url });
  }

  async post<T = any>(url: string, data?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>({ ...options, method: 'POST', url, data });
  }

  async put<T = any>(url: string, data?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>({ ...options, method: 'PUT', url, data });
  }

  async delete<T = any>(url: string, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>({ ...options, method: 'DELETE', url });
  }

  async patch<T = any>(url: string, data?: any, options?: HttpRequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>({ ...options, method: 'PATCH', url, data });
  }

  // Handle cancellation
  createCancelToken() {
    return axios.CancelToken.source();
  }

  // Private methods
  private setupInterceptors(): void {
    // Add a request interceptor to automatically add the auth token and tenant ID
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Ensure headers object exists
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const headers = (config.headers ??= {} as any);

        // Get the token from localStorage (synchronous for web)
        const token = LocalStorageService.getAccessToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;

        headers['X-Client-App'] = 'admin_desk';

        // Optional: Tenant-ID mitschicken, falls vorhanden (manche BE erwarten das weiterhin)
        const tenantId = LocalStorageService.getSelectedTenantId();
        if (tenantId) headers['Tenant-ID'] = tenantId;

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor to handle token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 (Unauthorized) and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const refreshToken = LocalStorageService.getRefreshToken();

            if (refreshToken) {
              // Use the base axios instance for token refresh to avoid circular interceptors
              const response = await axios.post(`${this.API_URL}/api/admin/token/refresh/`, {
                refresh: refreshToken
              });

              // Store the new access token
              LocalStorageService.setAccessToken(response.data.access);

              // Ensure headers on retry
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const hdrs = (originalRequest.headers ??= {} as any);
              hdrs['Authorization'] = `Bearer ${response.data.access}`;
              // Optional: Tenant-Header beim Retry beibehalten
              const tenantId = LocalStorageService.getSelectedTenantId();
              if (tenantId) hdrs['Tenant-ID'] = tenantId;

              // Retry the original request
              return this.axiosInstance.request(originalRequest);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);

            // Clear tokens
            LocalStorageService.clearAuthData();

            // Redirect to login (web-only)
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

// Create a singleton instance
const httpService = new HttpService();

export default httpService; 
